import { ExecuteReportValues } from "shared/types";
import {
  materialItemFilterValidation,
  multipleItemFilterValidation,
  multipleOrderFilterValidation,
  multiplePackagingFilterValidation,
  multipleQualityValidation,
  orderFilterValidation,
  packagingFilterValidation,
  reelLabValidation,
  driggingRiggValidation,
  stockValidation,
  multiOrdersPackagingFilterValidation,
  multiCompanyPackagingValidation,
  qualityAdhesionValidation,
  reelLabResultMultipleValidation,
} from "shared/schemas";
import { useMemo, useState } from "react";
import { adaptOrdersForExecuteReports } from "shared/adapters";
import {
  ReportItem,
  ReportType,
  useGetAllOrdersQuery,
  useGetAllReportsQuery,
  useGetReportCategoriesQuery,
} from "shared/api";
import groupBy from "lodash.groupby";
import { useDebouncedValue } from "./useDebouncedValue";

interface UseExecuteReportsDataProps {
  values: ExecuteReportValues;
  showAll?: boolean;
}

export type GroupedReports = Record<string, ReportItem[]>;

export const useExecuteReportsData = ({
  values,
  showAll = false,
}: UseExecuteReportsDataProps) => {
  const selectedPackageId = values.id;

  const { data: reportCategories } = useGetReportCategoriesQuery({});

  const { data: reports, isLoading: isLoadingReports } = useGetAllReportsQuery({
    expand: "report_category",
    ...(showAll ? {} : { visible: true }),
  });

  const isEmptyReportsResponse = reports?.length === 0;

  const selectedReport = useMemo(() => {
    return reports?.find((report) => report?.id === values?.id);
  }, [reports, values?.id]);

  const groupedQuests: Record<string, ReportItem[]> = useMemo(() => {
    if (!reports) return {};

    const grouped = groupBy(
      reports,
      (report) => report.report_category?.name || "Others"
    );

    reportCategories?.forEach((cat) => {
      if (!grouped[cat.name]) {
        grouped[cat.name] = [];
      }
    });

    const orderedGrouped: Record<string, ReportItem[]> = {};

    [...(reportCategories ?? [])]
      .sort((a, b) => a.order - b.order)
      .forEach((cat) => {
        if (grouped[cat.name]) {
          orderedGrouped[cat.name] = grouped[cat.name];
        }
      });

    if (grouped.Others) {
      orderedGrouped.Others = grouped.Others;
    }

    return orderedGrouped;
  }, [reports, reportCategories]);

  // Flag for get different filters
  const selectedReportsType = useMemo(() => {
    const selectedReport = reports?.find(
      (report) => report?.id === selectedPackageId
    );

    return selectedReport?.report_type as ReportType;
  }, [selectedPackageId]);

  const selectedTemplateFormat = useMemo(() => {
    return selectedReport?.template_format;
  }, [selectedReport]);

  const isMaterialItem =
    selectedReportsType === ReportType.MATERIAL_ITEM ||
    selectedReportsType === ReportType.REEL_LABEL;
  const isPackaging = selectedReportsType === ReportType.PACKAGING;
  const isMultiplePackaging =
    selectedReportsType === ReportType.MULTIPLE_PACKAGING;
  const isOrderType = selectedReportsType === ReportType.ORDER;
  const isMultipleOrderType = selectedReportsType === ReportType.MULTIPLE_ORDER;
  const isReelLabResultType =
    selectedReportsType === ReportType.LAB_REEL_ARCHIVE;
  const isReelLabResultMultipleType =
    selectedReportsType === ReportType.REEL_LAB_RESULT_MULTIPLE;
  const isMultipleQualityType =
    selectedReportsType === ReportType.MULTIPLE_QUALITY;
  const isMultipleItemType =
    selectedReportsType === ReportType.MULTIPLE_MATERIAL_ITEM;
  const isNotSupportedYetType =
    selectedReportsType === ReportType.MULTIPLE_MATERIAL;
  const isMultipleOrderDaily =
    selectedReportsType === ReportType.MANAGEMENT_DAILY ||
    selectedReportsType === ReportType.MANAGEMENT_WEEKLY;
  const isDriggingRigg = selectedReportsType === ReportType.DRIGGING_RIGG;
  const isStockType = selectedReportsType === ReportType.STOCK;
  const isMultipleOrdersAndPackaging =
    selectedReportsType === ReportType.MULTIPLE_ORDER_PACKAGING;
  const isMultipleCompanyPackaging =
    selectedReportsType === ReportType.MULTIPLE_COMPANY_PACKAGING;
  const isQualityAdhesion = selectedReportsType === ReportType.QUALITY_ADHESION;

  const [orderSearch, setOrderSearch] = useState("");
  const debouncedOrderSearch = useDebouncedValue(orderSearch, 400);

  const { data: ordersData, isLoading: isLoadingOrders } = useGetAllOrdersQuery(
    {
      limit: 20,
      order_code__icontains: debouncedOrderSearch || undefined,
    }
  );

  const adaptedOrders = useMemo(() => {
    return adaptOrdersForExecuteReports(ordersData?.results ?? []);
  }, [ordersData]);

  const conditionSchemaMap = [
    { condition: isMaterialItem, schema: materialItemFilterValidation },
    { condition: isPackaging, schema: packagingFilterValidation },
    {
      condition: isMultiplePackaging,
      schema: multiplePackagingFilterValidation,
    },
    { condition: isOrderType, schema: orderFilterValidation },
    { condition: isMultipleOrderType, schema: multipleOrderFilterValidation },
    { condition: isReelLabResultType, schema: reelLabValidation },
    {
      condition: isReelLabResultMultipleType,
      schema: reelLabResultMultipleValidation,
    },
    { condition: isMultipleQualityType, schema: multipleQualityValidation },
    { condition: isMultipleItemType, schema: multipleItemFilterValidation },
    { condition: isMultipleOrderDaily, schema: multipleOrderFilterValidation },
    { condition: isDriggingRigg, schema: driggingRiggValidation },
    { condition: isStockType, schema: stockValidation },
    {
      condition: isMultipleCompanyPackaging,
      schema: multiCompanyPackagingValidation,
    },
    {
      condition: isMultipleOrdersAndPackaging,
      schema: multiOrdersPackagingFilterValidation,
    },
    { condition: isQualityAdhesion, schema: qualityAdhesionValidation },
  ];

  return {
    selectedReport,
    groupedQuests,
    isLoadingReports,
    selectedTemplateFormat,
    isLoadingOrders,
    selectedFilter: {
      isMaterialItem,
      isPackaging,
      isMultiplePackaging,
      isOrderType,
      isMultipleOrderType,
      isNotSupportedYetType,
      isReelLabResultType,
      isReelLabResultMultipleType,
      isMultipleQualityType,
      isMultipleItemType,
      isMultipleOrderDaily,
      isDriggingRigg,
      isStockType,
      isMultipleOrdersAndPackaging,
      isMultipleCompanyPackaging,
      isQualityAdhesion,
    },
    adaptedOrders,
    onOrderSearch: setOrderSearch,
    conditionSchemaMap,
    isEmptyReportsResponse,
  };
};
